import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  buildGoogleConsentState,
  COOKIE_CONSENT_MAX_AGE_SECONDS,
  COOKIE_CONSENT_NAME,
  formatCookieConsent,
  hasAnyConsent,
  type ICookieConsentChoice,
  isConsentReduced,
  nowInSeconds,
  parseCookieConsent,
} from '../../lib/cookie-consent';
import { gtag } from '../../lib/google-tag';
import { type ILanguageState } from '../reducers/language/types';

export const COOKIE_CONSENT_REOPEN_EVENT = 'cookieConsentReopen';

const GTM_SCRIPT_ID = 'gtm-script';

type TBannerView = 'hidden' | 'initial' | 'settings';

interface IConsentDraft {
  analytics: boolean;
  marketing: boolean;
}

const readStoredChoice = (): ICookieConsentChoice | null => {
  if (window.__COOKIE_CONSENT__ !== undefined) {
    return window.__COOKIE_CONSENT__;
  }

  try {
    const match = document.cookie.match(/(?:^|; )cookieConsent=([^;]*)/);

    return parseCookieConsent(
      match ? decodeURIComponent(match[1]) : null,
      nowInSeconds(),
    ).choice;
  } catch {
    return null;
  }
};

const writeStoredChoice = (choice: ICookieConsentChoice): boolean => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  try {
    document.cookie = `${COOKIE_CONSENT_NAME}=${formatCookieConsent(choice)}; path=/; max-age=${COOKIE_CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
    return true;
  } catch {
    return false;
  }
};

const injectGtm = (gtmId: string): void => {
  if (document.getElementById(GTM_SCRIPT_ID)) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);
};

const applyGoogleConsent = (
  previous: ICookieConsentChoice | null,
  next: ICookieConsentChoice,
): void => {
  const state = buildGoogleConsentState(next);

  if (isConsentReduced(previous, next)) {
    gtag('consent', 'update', state);
    window.location.reload();
    return;
  }

  const gtmId = window.__GTM_ID__;

  if (!gtmId || !hasAnyConsent(next)) {
    return;
  }

  if (hasAnyConsent(previous) || document.getElementById(GTM_SCRIPT_ID)) {
    gtag('consent', 'update', state);
    return;
  }

  const queuedEvents = window.dataLayer?.splice(0) ?? [];
  gtag('consent', 'default', state);
  injectGtm(gtmId);
  window.dataLayer?.push(...queuedEvents);
};

function ConsentButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button type="button" className="cookie-consent__button" onClick={onClick}>
      {label}
    </button>
  );
}

function ConsentCategory({
  name,
  checked,
  isLocked,
  onChange,
}: {
  name: 'necessary' | 'analytics' | 'marketing';
  checked: boolean;
  isLocked: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const inputId = `cookie-consent-${name}`;

  return (
    <div className="cookie-consent__category">
      <label className="cookie-consent__category-title" htmlFor={inputId}>
        <input
          id={inputId}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={isLocked}
          onChange={(event): void => onChange(event.target.checked)}
        />
        {t(`cookieConsent.${name}Title`)}
        {isLocked ? (
          <span className="cookie-consent__always-on">
            {t('cookieConsent.alwaysOn')}
          </span>
        ) : null}
      </label>
      <p className="cookie-consent__text">
        {t(`cookieConsent.${name}Description`)}
      </p>
      <p className="cookie-consent__details">
        {t('cookieConsent.toolsLabel')}: {t(`cookieConsent.${name}Tools`)}
        <br />
        {t('cookieConsent.retentionLabel')}:{' '}
        {t(`cookieConsent.${name}Retention`)}
      </p>
    </div>
  );
}

function CookieConsentBanner({
  Language,
}: {
  Language: ILanguageState;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [view, setView] = useState<TBannerView>('hidden');
  const [choice, setChoice] = useState<ICookieConsentChoice | null>(null);
  const [draft, setDraft] = useState<IConsentDraft>({
    analytics: false,
    marketing: false,
  });
  const languageCode = Language.languageType.code.toLowerCase();

  useEffect((): void => {
    const storedChoice = readStoredChoice();
    setChoice(storedChoice);
    setView(storedChoice === null ? 'initial' : 'hidden');
  }, []);

  useEffect((): (() => void) => {
    const handleReopen = (): void => {
      setDraft({
        analytics: choice?.analytics ?? false,
        marketing: choice?.marketing ?? false,
      });
      setView('settings');
    };
    window.addEventListener(COOKIE_CONSENT_REOPEN_EVENT, handleReopen);
    return (): void =>
      window.removeEventListener(COOKIE_CONSENT_REOPEN_EVENT, handleReopen);
  }, [choice]);

  if (view === 'hidden') {
    return null;
  }

  const decide = (analytics: boolean, marketing: boolean): void => {
    const next: ICookieConsentChoice = {
      analytics,
      marketing,
      decidedAt: nowInSeconds(),
    };

    writeStoredChoice(next);
    setChoice(next);
    setView('hidden');
    applyGoogleConsent(choice, next);
  };

  const openSettings = (): void => {
    setDraft({
      analytics: choice?.analytics ?? false,
      marketing: choice?.marketing ?? false,
    });
    setView('settings');
  };

  return (
    <div
      className={`cookie-consent cookie-consent--${view}`}
      role="region"
      aria-label={t('cookieConsent.title')}
      aria-live="polite"
    >
      <div className="cookie-consent__content">
        <p className="cookie-consent__title">{t('cookieConsent.title')}</p>
        <p className="cookie-consent__text">
          {t('cookieConsent.intro')}{' '}
          <Link to={`/${languageCode}/privacy-policy#cookies`}>
            {t('cookieConsent.learnMore')}
          </Link>
        </p>
        {view === 'settings' ? (
          <div className="cookie-consent__categories">
            <ConsentCategory
              name="necessary"
              checked
              isLocked
              onChange={(): void => undefined}
            />
            <ConsentCategory
              name="analytics"
              checked={draft.analytics}
              isLocked={false}
              onChange={(analytics): void => setDraft({ ...draft, analytics })}
            />
            <ConsentCategory
              name="marketing"
              checked={draft.marketing}
              isLocked={false}
              onChange={(marketing): void => setDraft({ ...draft, marketing })}
            />
          </div>
        ) : null}
      </div>
      <div className="cookie-consent__actions">
        {view === 'settings' ? (
          <ConsentButton
            label={t('cookieConsent.save')}
            onClick={(): void => decide(draft.analytics, draft.marketing)}
          />
        ) : null}
        <ConsentButton
          label={t('cookieConsent.rejectAll')}
          onClick={(): void => decide(false, false)}
        />
        <ConsentButton
          label={t('cookieConsent.acceptAll')}
          onClick={(): void => decide(true, true)}
        />
        {view === 'initial' ? (
          <ConsentButton
            label={t('cookieConsent.settings')}
            onClick={openSettings}
          />
        ) : null}
      </div>
    </div>
  );
}

const mapStateToProps = ({ Language }: { Language: ILanguageState }) => ({
  Language,
});

export default connect(mapStateToProps)(CookieConsentBanner);
